import { find } from 'lodash';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  useController,
} from 'react-hook-form';
import theme from 'theme';

import { ErrorMessage } from '@hookform/error-message';
import ActionButton from 'components/buttons/ActionButton';
import ChevronIcon from 'components/icons/ChevronIcon';
import WarningIcon from 'components/icons/WarningIcon';
import ListForSelectingDataFE from 'components/listForSelectingData/lists/ListForSelectingDataFE';
import {
  ErrorText,
  InputContainer,
} from 'modules/auth/components/styledElements/formsStyledElements';

import {
  ActiveListParameter,
  CountryItem,
  DropdownInput,
  FormLabel,
} from '../../styledElements/formsStyledElements';

type ListItem = {
  id: string;
  displayName: string;
  BEformat: string;
  disabled?: boolean;
};

type ValidatedDropdownProps<T extends FieldValues = FieldValues> = {
  inputId: string;
  listLabel: string;
  inputLabel: string;
  inputValue: string;
  list: ListItem[];
  control: Control<T>;
  errors: FieldErrors;
  isDisabledDropdown?: boolean;
};

const ValidatedDropdown = <T extends FieldValues = FieldValues>({
  inputId,
  list,
  listLabel,
  inputValue,
  inputLabel,
  control,
  errors,
  isDisabledDropdown,
}: ValidatedDropdownProps<T>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name: inputId as Path<T>,
    control,
  });

  const [isListOpen, setIsListOpen] = useState(false);

  const closeList = useCallback(() => setIsListOpen(false), []);

  const openList = useCallback(
    () => !isDisabledDropdown && setIsListOpen(true),
    [isDisabledDropdown]
  );

  const saveChosenItem = useCallback(
    (index: string) => {
      const selected = list[Number(index)].BEformat;

      onChange(selected);
      closeList();
    },
    [list, onChange, closeList]
  );

  const hasError = !!error;
  const displayValue = value || inputValue;

  useLayoutEffect(() => {
    if (inputValue) {
      onChange(inputValue);
    }
  }, [inputValue, onChange]);

  return (
    <InputContainer $error={hasError} $disabled={isDisabledDropdown}>
      <FormLabel
        $isCursorPointer
        $active={!!displayValue}
        $error={hasError}
        onClick={openList}
      >
        {inputLabel}
      </FormLabel>

      <DropdownInput onClick={openList}>
        <CountryItem>
          <ActiveListParameter>
            {find(list, { BEformat: displayValue })?.displayName}
          </ActiveListParameter>
        </CountryItem>
        <ActionButton icon={<ChevronIcon color={theme.colors.funtainBlue} />} />
      </DropdownInput>

      {isListOpen && (
        <ListForSelectingDataFE
          listWidth="lg"
          listHeight="md"
          data={list}
          label={listLabel}
          closeList={closeList}
          setNewActiveListItem={saveChosenItem}
        />
      )}

      <ErrorMessage
        name={inputId}
        errors={errors}
        render={({ message }) => (
          <ErrorText $error={hasError}>
            <WarningIcon color={theme.colors.primary} />
            {message}
          </ErrorText>
        )}
      />
    </InputContainer>
  );
};

export default ValidatedDropdown;
